'use client';

import { useOnClickOutside } from 'usehooks-ts';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { PRODUCT_QUERY_PARAMS } from '@/features/navigation/constants';
import { useDrawer } from '@/features/drawer/context/use-drawer';
import { IconArrowRight1, IconProgressActivity } from '@/features/icons';
import { IconClose, IconSearchNormal } from '@/components/svg-symbols/icons';
import { useUserToken } from '@/app/_utils/session';
import { cn } from '@/utils/cn';
import { MIN_ACCEPTABLE_QUERY_LENGTH } from './constants';
import Backdrop from './backdrop';
import LastSearchHistory from './last-search-history';
import SearchResultChips from './search-result-chips';
import SearchResultProducts from './search-result-products';
import Suggestions from './suggestions';
import fetchSearchResult from './utils/fetch-search-results';
import { generateSearchUrl } from './utils/search-helpers';
import useSearchHistory from './utils/use-search-history';
import SearchSkeleton from '../components/search-skeleton';
import { SearchDrawerName } from '../constants';
interface Props {
  mode: 'mobile' | 'desktop';
}
const SearchBox = forwardRef<HTMLInputElement, Props>(function SearchBox({
  mode = 'desktop'
}, inputRef) {
  const formRef = useRef(null);
  const {
    closeDrawer
  } = useDrawer();
  const [ut] = useUserToken();
  const searchParams = useSearchParams();
  const queryParam = searchParams.get(PRODUCT_QUERY_PARAMS.SEARCH);
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 300);
  const [historyItems, addHistoryItem] = useSearchHistory();
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const isMobile = mode === 'mobile';
  const pathname = usePathname();
  const handleInputFocus = () => {
    try {
      // @ts-expect-error: Goftino doesn't include types
      Goftino.close();
    } catch (error) {
      // nothing
    }
    return setIsFocus(true);
  };

  // check if route changed, force close the search resualt container
  useEffect(() => {
    setIsFocus(false);
  }, [pathname]);
  useOnClickOutside(formRef, () => {
    if (isMobile) return;
    setIsFocus(false);
  });
  const {
    isLoading,
    isError,
    data: response,
    isFetching
  } = useQuery({
    queryKey: ['search', {
      ut,
      debouncedQuery
    }],
    queryFn: ({
      signal
    }) => fetchSearchResult({
      ut,
      query: debouncedQuery,
      signal
    }),
    enabled: (debouncedQuery.length === 0 || debouncedQuery.length > MIN_ACCEPTABLE_QUERY_LENGTH) && !!ut,
    placeholderData: keepPreviousData
  });
  const results = response && response.isSuccess ? response.data : null;
  const noResult = !results?.brands?.length && !results?.categories?.length && !results?.products?.length && !(isFetching && isLoading);
  const queryId = results?.queryId;
  const handleSubmit = (e: React.FormEvent) => {
    if (!query.length) {
      e.preventDefault();
      if (mode === 'mobile') return;
      setIsFocus(false);
      if (inputRef && typeof inputRef === 'object' && 'current' in inputRef) {
        inputRef.current?.blur();
      }
      return false;
    }
    setQuery(query.trim());
    addHistoryItem(query);
    return true;
  };
  useEffect(() => {
    setIsFocus(isMobile);
  }, [mode, setIsFocus, isMobile]);
  useEffect(() => {
    setQuery(queryParam || '');
    setIsFocus(false);
  }, [queryParam]);
  return <>
            <form className={cn('relative z-top-of-chatbot-plus3 mx-auto max-w-xl grow flex-wrap rounded border-none lg:flex', isMobile ? 'flex' : 'hidden')} autoComplete='off' ref={formRef} data-test='search-form' onSubmit={handleSubmit} action={generateSearchUrl()}>
                <div className='relative mb-5 flex h-14 w-full items-center justify-center overflow-hidden rounded lg:mb-0 lg:h-auto'>
                    <div className='absolute right-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                        <IconSearchNormal className='mr-8 h-6 w-6 text-text-mediumGray lg:mr-1' />
                    </div>

                    <div className='absolute left-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                        {isFetching && query.length ? <IconProgressActivity className='mr-1 h-6 w-6 animate-spin text-text-mediumGray' /> : <button type='button' onClick={() => setQuery('')} data-test='clear-result' className={cn('flex text-text-mediumGray', {
            hidden: !query.length
          })}>
                                <IconClose className='mr-1 h-6 w-6' />
                            </button>}
                    </div>

                    {isMobile && <label onClick={() => closeDrawer(SearchDrawerName)} className='relative z-10 ml-1 cursor-pointer'>
                            <IconArrowRight1 className='h-6 w-6 text-text-black' />
                        </label>}

                    <input ref={inputRef} value={query} type='text' autoComplete='off' onFocus={handleInputFocus} placeholder='جست‌وجو در کالاها، دسته‌بندی‌ها و برندها' className='h-11 flex-1 rounded border border-border-gray-medium py-2.5 pl-2 pr-10 text-body-14 leading-relaxed outline-none placeholder:text-text-mediumGray focus:border-pink-200 md:min-w-[520px] lg:text-body-16' onChange={e => setQuery(e.target.value.trimStart())} name='query' />
                </div>

                <div data-test='search-result' className={cn('z-10 w-full rounded lg:absolute lg:right-0 lg:top-full lg:mr-0 lg:mt-1 lg:overflow-y-auto lg:border lg:border-border-gray-medium lg:bg-white lg:px-2 lg:py-3 lg:shadow-md', isFocus ? 'flex' : 'hidden')}>
                    {isFocus && <div className='relative w-full leading-4 lg:mt-0'>
                            {isFetching && query.length ? <SearchSkeleton className='lg:mt-2' /> : <div>
                                    <span data-test='search-result--empty' className={cn('mb-18 text-body-14 text-text-mediumGray lg:mb-7 lg:px-1 lg:text-body-16', query.length > MIN_ACCEPTABLE_QUERY_LENGTH && noResult && !isError ? 'block' : 'hidden')}>
                                        برای جست‌وجوی شما نتیجه‌ای پیدا نشد!
                                    </span>

                                    {isError && query.length && <span data-test='search-result--error' className='mb-2 mr-6 text-body-14 lg:px-4 lg:text-body-16'>
                                            جست‌وجو با خطا مواجه شد.
                                        </span>}

                                    {!isError && <>
                                            {results?.categories && <SearchResultChips dataTest='categories' items={results.categories} title='دسته‌بندی‌ها' />}

                                            {results?.brands && <SearchResultChips dataTest='brands' items={results.brands} title='برندها' />}

                                            {!query.length && !results?.queryId && historyItems && <LastSearchHistory items={historyItems} title='آخرین جست‌وجوهای شما' />}

                                            <Suggestions items={results?.popularSearch || []} title='جست‌وجوهای پرطرفدار' />

                                            <SearchResultProducts query={query} queryId={queryId} items={results?.products || null} title='محصولات' />
                                        </>}
                                </div>}
                        </div>}
                </div>
            </form>
            {isFocus && <Backdrop />}
        </>;
});
export default SearchBox;